import { Injectable, OnDestroy, signal } from "@angular/core";
import { AngularFirestore, DocumentChangeAction } from "@angular/fire/compat/firestore";
import {
  collection,
  doc,
  docData,
  docSnapshots,
  DocumentData,
  DocumentSnapshot,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { encryptUsingAES256 } from "@core/utility.service";
import * as dayjs from "dayjs";
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from "rxjs";
import { catchError, delay, map, shareReplay, startWith, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { CommonService } from "src/app/services/common/common.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { HomeDataService } from "src/app/services/home-data.service";
import { NarratorService } from "src/app/services/narrator.service";
import { NotificationManagerService } from "src/app/services/notification-manager.service";
import { RequestGameService } from "src/app/services/RequestBodyService/RequestGame/request-game.service";
import { UserManagementService } from "src/app/services/user-management.service";
import { environment } from "src/environments/environment";

export interface PremiumGameMapping {
  baseGameId: number;
  premiumGameId: number;
  status: "live" | "development";
}

export const PREMIUM_GAME_MAPPINGS: PremiumGameMapping[] = [
  { baseGameId: 517, premiumGameId: 678, status: 'live' },        // Splash of Luck
  { baseGameId: 324,   premiumGameId: 674,   status: 'live' },        // Spell of Fortune
  { baseGameId: 343,   premiumGameId: 687,   status: 'live' }, // A Fair Chance
  { baseGameId: 545,   premiumGameId: 694,   status: 'live' }, // Master of the Rings
];

const zeroIndex = 0;
@Injectable({
  providedIn: "root",
})
export class GamesFeedSampleService implements OnDestroy {
  private destroySubscription$ = new Subject<void>();
  private gameDataSignal = signal<Data>(null);
  private devGameDataSignal = signal<Data>(null);
  public gameDataProfile = signal<Data>(null);
  private dataSignal = signal<Data>(undefined);
  public getGamePrizeBoxSettings = signal<Data>(null);
  public userGamePoints: number;
  yourToPlayGame$ = new BehaviorSubject({});
  yoursToPlay$ = new BehaviorSubject([]);
  popupDisabled$ = new BehaviorSubject(false);
  rfRemoteConfig$ = new BehaviorSubject(true);
  isY2pPopup = new BehaviorSubject<boolean>(false);
  isGamePopupVisible = false;
  updateMainPageSub: Subscription;
  updateMainpageSubTwo: Subscription;
  updateMainPageSubThree: Subscription;
  gameMiniDataService$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      return user
        ? this.catchService.isOnline$
          .pipe(
            switchMap(async (isOnline) => {
              if (!isOnline) return [];

              const q = query(
                collection(this.firestore, `/game_mini_doc/`),
                orderBy("category", "desc") // Order documents by category descending
              );

              const querySnapshot = await getDocs(q); // Fetch documents based on the query
              return querySnapshot.docs.map((doc) => ({
                type: "added", // Add a default type to mimic DocumentChangeAction
                payload: { doc }, // Mimic AngularFirestore snapshot structure
              }));
            }),
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            shareReplay(1)
          )
          .pipe(map((actions: any[]) => this.snapShotData(actions))) // Pass the mapped data to snapShotData
        : of([]);
    })
  );

  getActiveGameIds$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      if (!user) return of([]);

      return new Observable<Data>((observer) => {
        const docRef = doc(this.firestore, "general_modules/game");

        const unsubscribe = onSnapshot(
          docRef,
          (snapshot) => {
            observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
          },
          (error) => observer.error(error)
        );

        // Cleanup subscription on unsubscribe
        return () => unsubscribe();
      }).pipe(shareReplay(1));
    })
  );

  getGameFlashSale$ = new Observable<Data>((observer) => {
    const docRef = doc(this.firestore, "general_modules/games_flash_sale");

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
      },
      (error) => observer.error(error)
    );

    return () => unsubscribe();
  }).pipe(shareReplay(1));

  rapidFireGamesOrder$ = defer(() =>
    new Observable<Data>((observer) => {
      const docRef = doc(this.firestore, "Global_lookup/games_order");

      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => {
          observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
        },
        (error) => observer.error(error)
      );

      return () => unsubscribe();
    }).pipe(shareReplay(1))
  );

  getActiveGameList$ = combineLatest([this.gameMiniDataService$, this.getActiveGameIds$, this.rapidFireGamesOrder$]);

  y2Play$ = this.userManagementService?.userRequest$.pipe(
    switchMap((user) =>
      user
        ? this.catchService.isOnline$
          .pipe(
            switchMap((isOnline) => {
              if (!isOnline) return of([]);
              return this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  if (!userId) return of([]);
                  const docRef = doc(this.firestore, `/users/${userId}/game/yours_to_play`);
                  const collectionRef = collection(docRef, "details"); // Reference Firestore sub-collection
                  const q = query(collectionRef); // Query for the collection

                  // Convert Promise to Observable using defer and from
                  return defer(() => getDocs(q)).pipe(
                    map((querySnapshot) =>
                      querySnapshot.docs.map((docSnap) => ({
                        type: "added", // Add a default type to mimic DocumentChangeAction
                        payload: { doc: docSnap }, // Mimic AngularFirestore snapshot structure
                      }))
                    )
                  );
                })
              );
            }),
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            shareReplay(1)
          )
          .pipe(
            map((actions: any[]) => this.snapShotData(actions)),
            shareReplay(1),
            tap((data) => {
              // Handle onSnapshot side effects in tap operator
              try {
                let counter = 0;
                if (data && Array.isArray(data)) {
                  let gameYourPlay = [];
                  data?.forEach((game) => {
                    if (!game || !game?.referance_doc) {
                      counter++;
                      return;
                    }
                    onSnapshot(game?.referance_doc, (ref) => {
                      game["details"] = ref?.data();
                      counter++;
                      if (game?.play_count > 0 && game?.details?.game_type_id != 5) {
                        if (game.game_id == this.prizeUnlockID) {
                          gameYourPlay.splice(0, 0, game);
                        } else {
                          if (game?.catagory != "unlock") {
                            if (game?.details?.game_status == 1) {
                              gameYourPlay?.push(game);
                            }
                          } else {
                            gameYourPlay.push(game);
                          }
                        }
                      }
                      if (counter == data?.length) {
                        this.yoursToPlay$.next(gameYourPlay);
                      }
                    });
                  });
                }
              } catch (e) {
                console.error("Error in y2Play$ tap operator:", e);
              }
            })
          )
        : of([])
    )
  );

  randomGames: boolean;
  public randomIndexes: Data;
  public localStorageKey: string;

  public getY2pPopupStatus() {
    return this.isY2pPopup?.asObservable();
  }

  public shouldShowPremiumToggle(gamePreviewData: Data): boolean {
    if (!gamePreviewData) {
      return false;
    }
    const gameId = Number(gamePreviewData?.game_id);
    if (!Number.isFinite(gameId) || gameId <= 0) {
      return false;
    }
    const mapping = PREMIUM_GAME_MAPPINGS.find(
      (m) => m.baseGameId === gameId || m.premiumGameId === gameId
    );
    if (!mapping) {
      return false;
    }

    return mapping.status === 'live' || environment.showDevelopmentPremiumGames;
  }

  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction<unknown>[]): Data[] {
    if (!actions || !Array.isArray(actions)) {
      return [];
    }

    return actions.map((a) => {
      const data = (a?.payload.doc.data() as Data) ?? {};
      const id = a?.payload.doc?.id;
      return { id, ...data };
    });
  }

  prizeUnlockID: string;
  userId: any; //this var is contain the user id from local storage.
  playGameStatus: boolean;
  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private catchService: CatchErrorService,
    private narratorService: NarratorService,
    private matDialog: MatDialog,
    private GTMService: GTMSagmentService,
    private router: Router,
    private RequestGameService: RequestGameService,
    private userManagementService: UserManagementService,
    private homeDataService: HomeDataService,
    private notificationManagerService: NotificationManagerService,
    private afs: AngularFirestore,
    private commonService: CommonService,
    private authStateService: AuthStateService
  ) {
    this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        this.userId = userId || null;
        return of(userId) || of(null);
      })
    );
    const routerSubscription = this.router.events
      .pipe(startWith(this.router), takeUntil(this.destroySubscription$))
      ?.subscribe((event: Data) => {
        if (event instanceof NavigationEnd) {
          if (event?.url?.includes("/mainTabs/tabs/game-new")) {
            this.fetchGameData('game_new');
            this.fetchDevGameData('game_new');
            routerSubscription.unsubscribe();
          } else if (event?.url?.includes("/mainTabs/tabs/game") && !event?.url?.includes("game-new")) {
            this.fetchGameData('game');
            routerSubscription.unsubscribe();
          }
        }
      });
    this.homeDataService.homeCardsVM$
      .pipe(
        catchError((err) => {
          console.error('Error in homeCardsVM$ subscription (games-feed-sample.service):', err);
          return of(null);
        }),
        takeUntil(this.destroySubscription$)
      )
      .subscribe((data: Data) => {
        if (data && data.new_member_checklist && data.new_member_checklist.content) {
          const checkListCard = data.new_member_checklist.content.checklist;
          if (Array.isArray(checkListCard) && checkListCard.length > 0) {
            const item = checkListCard.find((item: Data) => item.title === "Play a Game");
            if (item) {
              this.playGameStatus = item?.status;
            }
          }
        }
      });
  }
  ngOnDestroy() {
    this.destroySubscription$?.next();
    this.destroySubscription$?.complete();
  }
  userRapidFireDetails$ = this.userManagementService?.userRequest$.pipe(
    switchMap(() =>
      this.commonService.getUid("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of({
              success: false,
              code: "USER_ID_IS_NULL",
              message: "User ID is null",
              data: {
                rapid_fire: {
                  star_token: 0,
                  points: 0,
                  rubies: 0
                }
              }
            });
          }

          // ✅ Use userId directly, no assignment to this.userId
          return new Observable<{ success: boolean; data?: any; code?: string; message?: string }>((observer) => {
            const docRef = doc(this.firestore, `/users/${userId}/balance/current`);
            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  observer.next({ success: true, data: snapshot.data() });
                } else {
                  observer.next({
                    success: false,
                    code: "DOCUMENT_NOT_FOUND",
                    message: "Document does not exist",
                  });
                }
              },
              (error) => {
                console.error(error);
                observer.next({
                  success: false,
                  code: "UNKNOWN_ERROR",
                  message: error.message,
                });
              }
            );

            return () => unsubscribe();
          });
        }),
        catchError(() => {
          return of({
            success: false,
            code: "CATCH_ERROR",
            message: "Error in observable chain",
            data: {
              rapid_fire: {
                star_token: 0,
                points: 0,
                rubies: 0
              }
            }
          });
        }),
        shareReplay(1)
      )
    ),
    startWith({
      success: false,
      code: "INITIALIZING",
      message: "Initializing",
      data: {
        rapid_fire: {
          star_token: 0,
          points: 0,
          rubies: 0
        }
      }
    })
  );

  rapidFireJackpot$ = this.userManagementService?.userRequest$.pipe(
    switchMap(() => {
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of({});
          }

          return new Observable<Data>((observer) => {
            const docRef = doc(this.firestore, "jackpot/RapidFire");

            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
              },
              (error) => {
                console.error("[rapidFireJackpot$] Error:", error);
                observer.next({});
              }
            );

            return () => unsubscribe();
          });
        }),
        catchError((error) => {
          console.error("[rapidFireJackpot$] Catch Error:", error);
          return of({});
        }),
        shareReplay(1)
      );
    }),
    startWith({})
  );

  getUserCurrentBalance(): Observable<{ success: boolean; data?: any; code?: string; message?: string }> {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of({ success: false, code: "USER_ID_NOT_FOUND", message: "User ID not found in localStorage" });
        }

        // Gate on customTokenReady$ before opening the onSnapshot listener so it
        // never fires before the Firebase custom token is confirmed stored,
        // preventing the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => new Observable<{ success: boolean; data?: any; code?: string; message?: string }>((observer) => {
            const docRef = doc(this.firestore, `users/${userId}/balance/current`);

            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  observer.next({ success: true, data: snapshot.data() });
                } else {
                  observer.next({
                    success: false,
                    code: "DOCUMENT_NOT_FOUND",
                    message: "Document does not exist",
                  });
                }
              },
              (error) => {
                console.error("Error fetching user current balance:", error);
                observer.next({
                  success: false,
                  code: "UNKNOWN_ERROR",
                  message: error.message,
                });
              }
            );

            // Clean up the listener when unsubscribed
            return () => unsubscribe();
          }))
        ).pipe(shareReplay(1));
      })
    );
  }

  // Lucky Numbers
  luckyNumberJackpot$ = this.userManagementService?.userRequest$.pipe(
    switchMap(() => {
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of({});
          }

          return new Observable<Data>((observer) => {
            const docRef = doc(this.firestore, "jackpot/LuckyNumbers");

            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
              },
              (error) => {
                console.error("[luckyNumberJackpot$] Error:", error);
                observer.next({});
              }
            );

            return () => unsubscribe();
          });
        }),
        catchError((error) => {
          console.error("[luckyNumberJackpot$] Catch Error:", error);
          return of({});
        }),
        shareReplay(1)
      );
    }),
    startWith({})
  );
  // Millionaire Moments
  millionaireMomentsJackpot$ = this.userManagementService?.userRequest$.pipe(
    switchMap(() => {
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of({});
          }

          return new Observable<Data>((observer) => {
            const docRef = doc(this.firestore, "jackpot/MillionaireMoments");

            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                observer.next(snapshot.exists() ? (snapshot.data() as Data) : {});
              },
              (error) => {
                console.error("[millionaireMomentsJackpot$] Error:", error);
                observer.next({});
              }
            );

            return () => unsubscribe();
          });
        }),
        catchError((error) => {
          console.error("[millionaireMomentsJackpot$] Catch Error:", error);
          return of({});
        }),
        shareReplay(1)
      );
    }),
    startWith({})
  );
  getRapidFireDetails$ = combineLatest([
    this.userRapidFireDetails$,
    this.rapidFireJackpot$,
    this.luckyNumberJackpot$,
    this.millionaireMomentsJackpot$,
  ]);

  getGamesData(): Observable<any> {
    const docRef = doc(this.firestore, "general_modules/game");

    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            observer.next(data);
          } else {
            this.authService.getConstantsMsg("1014").then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            });
            observer.next(null); // emit null instead of error to continue observable chain
          }
        },
        (error) => {
          observer.error(error);
        }
      );

      return () => unsubscribe();
    }).pipe(
      catchError(() => {
        return of(null);
      }),
      shareReplay(1)
    );
  }

  getGamePopupData(game: string, gameId: string): Observable<any> {
    if (!game || !gameId) {
      return of([]);
    }
    const path = `general_modules/game/${environment.mirrorPath}${game}/${gameId}`;
    const docRef = doc(this.firestore, path);

    return new Observable((observer) => {
      const unsubscribe = onSnapshot(
        docRef,
        (docSnap) => {
          if (docSnap.exists()) {
            observer.next(docSnap.data());
          } else {
            observer.next(null);
          }
        },
        (error) => {
          observer.error(error);
        }
      );

      return () => unsubscribe();
    }).pipe(shareReplay(1));
  }

  //   User Promotional Slider
  userPromotionalSlide$ = this.userManagementService?.userRequest$?.pipe(
    switchMap(() => {
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          if (!userId) return of([]);

          return new Observable<DocumentData | undefined>((observer) => {
            const docRef = doc(this.firestore, `users/${userId}/game_promotion/promotional_slider`);

            const unsubscribe = onSnapshot(
              docRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  observer.next(snapshot.data());
                } else {
                  observer.next(undefined);
                }
              },
              (error) => observer.error(error)
            );

            return () => unsubscribe();
          }).pipe(shareReplay(1));
        })
      );
    })
  );

  // Game Promotional Slider
  gamePromotionalSlide$ = defer(() =>
    this.userManagementService?.userRequest$?.pipe(
      switchMap(() =>
        this.authService
          .getAngularFireStore(`/general_modules/game_promotional_slider/game_slider/slider_1`, "snapshotChanges")
          .pipe(
            map((res: Data) => res?.payload?.data()),
            shareReplay(1)
          )
      )
    )
  );
  // This method is defined for get second card of game via retool
  secondSlidePromo$ = defer(() => {
    const path = "/general_modules/game_promotional_slider/game_slider/slider_second";

    return this.userManagementService?.userRequest$?.pipe(
      switchMap(() =>
        this.authService?.getAngularFireStore(path, "snapshotChanges").pipe(
          map((res: Data) => res?.payload?.data()),
          shareReplay(1)
        )
      )
    );
  });

  // This method is defined for get second card of dynamic game
  gameForDynamicSlide$ = defer(() => {
    const path = "/general_modules/game_promotional_slider/promotional_slider_dynamic_game/dynamic_games";

    return this.userManagementService.userRequest$.pipe(
      switchMap(() =>
        this.authService.getAngularFireStore(path, "snapshotChanges").pipe(
          map((res: Data) => {
            if (!res) {
              throw new Error("Data not found");
            }
            return res.payload.data();
          }),
          catchError(() => of(null)), // Return null gracefully on error
          shareReplay(1)
        )
      )
    );
  });

  getPromotionalSliderData$ = combineLatest([
    this.gamePromotionalSlide$,
    this.secondSlidePromo$,
    this.userPromotionalSlide$,
  ]).pipe(
    map((data: Array<Data>) => {
      let PromotionalSlideData = [];
      const userPromotionalSlide = data[2]?.slides?.slice(1)?.flat(1) ?? [];
      PromotionalSlideData?.push(data[0]);
      PromotionalSlideData?.push(data[1]);
      PromotionalSlideData?.push(...userPromotionalSlide);
      return PromotionalSlideData;
    }),
    shareReplay(1)
  );

  getRapidFireStatus() {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        return userId
          ? this.authService.getAngularFireStore(`/users/${userId}/special_features/features`, "snapshotChanges").pipe(
            map((data: Data) => data?.payload?.data()),
            shareReplay(1)
          )
          : of([]);
      })
    );
  }

  getUserGameInfo(gameDetails) {
    return new Promise((resolve, reject) => {
      if (gameDetails?.game_id && window.navigator.onLine && !gameDetails?.user_game_restriction) {
        this.puchaseFirebaseGame(gameDetails)
          ?.pipe(takeUntil(this.destroySubscription$))
          ?.subscribe(
            (res) => {
              const encryptedToken = encryptUsingAES256(localStorage.getItem("fbToken"));
              if (res && res?.status?.toLowerCase() === "success") {
                res.play_url = res?.play_url + "&token=" + encryptedToken;
                resolve(res);
              } else {
                reject(res);
              }
            },
            (err) => {
              reject(err);
            }
          );
      } else return resolve({});
    });
  }

  puchaseFirebaseGame(game: any) {
    try {
      return this.RequestGameService.getPurchaseFiebaseGameRequest(game).pipe(
        switchMap((payload) =>
          this.authService.getV2CloudFunction("apiv2", payload).pipe(
            map((res: Data) => {
              if (res?.status === "success" && res?.play_url !== "") {
                const game_url = res?.play_url;
                const regex = /\/games\/([^\/?]+)/;
                const match = game_url.match(regex);
                if (match && match.length >= 2) {
                  this.GTMService.trackGamesView(match[1]);
                }
              }
              return res?.data || res;
            }),
            catchError((error) => {
              this.authService.getConstantsMsg("1015").then((translatedMsg) => {
                const defaultMsg = {
                  message: { line_1: "something went wrong!", line_2: "" },
                  style: { icon: "tick circle error.svg", type: "error" },
                };
                this.authService.showToastMessage(translatedMsg, defaultMsg);
              });
              return throwError(error);
            })
          )
        )
      );
    } catch (error) {
      return throwError(error);
    }
  }

  // This method is used to get the last played game session
  getGamePlayLastSession() {
    let userId = null;
    this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        userId = userId;
        return of(userId) || of(null);
      })
    );
    if (!userId) return;

    const gameRefPath = `/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation_retool/details`;
    const recommendationPath = `/users/${userId}/game/recommendation`;

    this.authService
      ?.getAngularFireStore(gameRefPath, "snapshotChanges")
      ?.pipe(takeUntil(this.destroySubscription$))
      .subscribe(async (res: Data) => {
        try {
          const data = res?.payload?.data() as Data;
          this.randomGames = data?.randomise;

          if (data?.override_recommendation === true) {
            if (!data || !data?.detail) return;

            const gameDataArray = await Promise.all(data?.detail.map((g) => this.getData(g)));

            let finalData = data?.detail.map((g, index) => ({
              ...g,
              gameData: gameDataArray[index],
            }));
            // Randomize finalData if the randomise flag is true
            if (data?.randomise === true) {
              this.localStorageKey = "recommendation_game_random";
              // Generate random indices for finalData
              if (!localStorage.getItem(this.localStorageKey)) {
                this.randomIndexes = this.generateRandomIndices(finalData?.length);
                localStorage.setItem(this.localStorageKey, JSON.stringify(this.randomIndexes));
              } else {
                this.randomIndexes = JSON.parse(localStorage.getItem(this.localStorageKey));
              }
              // Map finalData to a new array using the random indices
              finalData = this.randomIndexes?.map((index) => finalData[index]);
            }

            this.yourToPlayGame$.next(finalData);
          } else {
            this.authService
              ?.getAngularFireStore(recommendationPath, "snapshotChanges")
              .pipe(
                map((res: Data) => res?.payload?.data()),
                catchError((err) => {
                  return err;
                }),
                takeUntil(this.destroySubscription$)
              )
              ?.subscribe((data) => {
                this.getGameRecommendation(data);
              });
          }
        } catch (e) {
          console.error(e);
        }
      });
  }

  getGameRecommendation(gameId) {
    this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          if (!isOnline) {
            return of(null);
          }
          return getDoc(
            doc(
              this.firestore,
              `/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation`,
              gameId && gameId.parent_game_id ? String(gameId.parent_game_id) : "default"
            )
          ).then((docSnap) => docSnap.data()); // Fetch document data
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: Data) => res),
        takeUntil(this.destroySubscription$)
      )
      ?.subscribe(async (data: Data) => {
        if (!data) {
          this.catchService.isOnline$
            .pipe(
              switchMap((isOnline) => {
                if (!isOnline) {
                  return of(null);
                }
                return getDoc(
                  doc(
                    this.firestore,
                    `/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation`,
                    "default"
                  )
                ).then((docSnap) => docSnap.data()); // Fetch default document data
              }),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              shareReplay(1)
            )
            .pipe(
              map((res: Data) => res),
              takeUntil(this.destroySubscription$)
            )
            ?.subscribe(
              async (d: Data) => {
                if (!d || !d?.detail) return;
                for (let g of d?.detail) {
                  const gameData = await this.getData(g);
                  g["gameData"] = gameData;
                }
                let finalData = d?.detail;
                // Randomize finalData if the randomise flag is true
                if (this.randomGames === true) {
                  if (!localStorage.getItem(this.localStorageKey)) {
                    this.randomIndexes = this.generateRandomIndices(finalData?.length);
                    try {
                      localStorage.setItem(this.localStorageKey, JSON.stringify(this.randomIndexes));
                    } catch (error) {
                      console.error("Failed to serialize randomIndexes:", error);
                    }
                  } else {
                    try {
                      this.randomIndexes = JSON.parse(localStorage.getItem(this.localStorageKey));
                    } catch (error) {
                      console.error("Failed to parse randomIndexes from localStorage:", error);
                      this.randomIndexes = this.generateRandomIndices(finalData?.length);
                    }
                  }
                  // Map finalData to a new array using the random indices
                  finalData = this.randomIndexes?.map((index) => finalData[index]);
                }
                this.yourToPlayGame$.next(finalData);
                return;
              },
              (err) => {
                return err;
              }
            );
        }
        if (!data || !data?.detail) return;
        for (let g of data?.detail) {
          const gameData = await this.getData(g);
          g["gameData"] = gameData;
        }
        let finalData = data?.detail;
        // Randomize finalData if the randomise flag is true
        if (this.randomGames === true) {
          if (!localStorage.getItem(this.localStorageKey)) {
            this.randomIndexes = this.generateRandomIndices(finalData?.length);
            try {
              localStorage.setItem(this.localStorageKey, JSON.stringify(this.randomIndexes));
            } catch (error) {
              console.error("Failed to serialize randomIndexes:", error);
            }
          } else {
            try {
              this.randomIndexes = JSON.parse(localStorage.getItem(this.localStorageKey));
            } catch (error) {
              console.error("Failed to parse randomIndexes from localStorage:", error);
              this.randomIndexes = this.generateRandomIndices(finalData?.length);
            }
          }
          // Map finalData to a new array using the random indices
          finalData = this.randomIndexes?.map((index) => finalData[index]);
        }
        this.yourToPlayGame$.next(finalData);
      });
  }

  // Function to generate a random permutation of indices
  generateRandomIndices(length: number) {
    const indices = Array.from({ length }, (_, i) => i);
    for (let i = length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }

  getData(gRef: Data) {
    return new Promise((resolve, reject) => {
      gRef?.game_ref.onSnapshot(
        (s) => {
          const data = s?.data();
          resolve(data);
        },
        (error) => {
          console.error(error);
          reject(error);
        }
      );
    });
  }

  prizeUnlock$ = this.catchService.isOnline$
    .pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return of([]);
        }
        return getDocs(collection(this.firestore, `/general_modules/game/${environment.mirrorPath}prize_unlock`)) // Fetch documents from the collection
          .then((querySnapshot) => querySnapshot.docs.map((docSnap) => docSnap)); // Map to document snapshots
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    )
    .pipe(
      map((res: any[]) => {
        res?.map((prizeU: any) => {
          this.prizeUnlockID = prizeU?.id; // Get the document ID
        });
        return this.prizeUnlockID;
      })
    );

  getGameInfoY2P(gameId: string, type?: string) {
    return this.catchService.isOnline$
      .pipe(
        switchMap((isOnline) => {
          return this.authService.getUid$("GamesFeedSampleService").pipe(
            switchMap((userId) => {
              if (!isOnline) {
                return of(null);
              }
              return getDoc(
                doc(
                  this.firestore,
                  `/users/${userId}/game/${type}/details/${gameId}` // Directly specify the document path
                )
              ).then((docSnap) => docSnap.data()); // Fetch the document data
            })
          );
        }),
        this.catchService.retryWhen,
        this.catchService.catchOfflineError,
        shareReplay(1)
      )
      .pipe(
        map((res: Data) => {
          try {
            return res;
          } catch (e) {
            return e;
          }
        })
      );
  }
  setTokenAnimationValue() {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        if (!userId) return;
        const path_id = `bE9fhvIi6G7dzDk8RO23`;
        const doc_data = { processed: true, token_count: 0 };
        const method = "update";

        return this.authService
          .getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          })
          .pipe(
            take(1),
            tap({
              error: (err) => console.error("Error updating token animation doc:", err),
            })
          );
      })
    );
  }

  narratorData: Data;
  customTest: boolean;
  public narratorShow() {
    try {
      this.narratorService
        ?.getNarratorTipsHome()
        ?.pipe(takeUntil(this.destroySubscription$))
        .subscribe(
          (res: Data) => {
            const narratorTipArray: Data = res;
            this.narratorData = [];
            // check narratorTipArray if it is not empty then show narrator tips
            if (narratorTipArray) {
              narratorTipArray.find((response: Data) => {
                if (
                  response &&
                  response?.setting?.page_name == this.narratorService?.getCurrentPageURL() &&
                  response?.setting?.seen == false
                ) {
                  return (this.narratorData = response);
                }
              });
            }
            if (
              this.narratorData?.setting?.seen == false &&
              this.narratorData?.setting?.page_name == this.narratorService?.getCurrentPageURL()
            ) {
              this.customTest = true;
              this.notificationManagerService.addNotification({ ...this.narratorData, type: "narrator_tip" });
            }
            if (
              this.customTest == true &&
              this.playGameStatus == false &&
              this.narratorData?.setting?.seen == undefined
            ) {
              this.customTest = false;
            }
          },
          (error) => {
            // handle error when api failed to respond
            return { success: false, code: "Api failed to respond", message: "Api failed to respond" };
          }
        );
    } catch (err) {
      // catch error when some functionality fail
      return { success: false, code: "Api failed to respond", message: "Api failed to respond" };
    }
  }

  //This method is used to get prize box data
  // This method is used to get prize box data
  getPrizeBoxGameData(): Observable<Data[]> {
    return new Observable<Data[]>((observer) => {
      getDocs(collection(this.firestore, `/general_modules/game/${environment.mirrorPath}prize_box`)) // Fetch the collection documents
        .then((querySnapshot) => {
          // Extract data from the snapshot
          const data = querySnapshot.docs.map((doc) => doc.data());
          observer.next(data);
          observer.complete();
        })
        .catch((error) => {
          // Handle error
          console.error("Error fetching prize box data:", error);
          observer.error(error);
        });
    });
  }

  setChallengesTooltip() {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null);
        const path_id = `aiUM9R8c5J9Zai5FQiOh`;
        const doc_data = { coachmark: { challenge_tooltip: true } };
        const method = "update";
        return this.authService
          .getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          })
          .pipe(
            map(() => userId),
            catchError(() => {
              return of(null);
            })
          );
      })
    );
  }

  getRapidFireData$: Observable<Data> = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      return user
        ? this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return []; // Return an empty array if offline

            const q = query(
              collection(this.firestore, `/general_modules/game/${environment.mirrorPath}rapid_fire`),
              where("game_status", "==", 1),
              where("is_avilable_to_purchase", "==", zeroIndex)
            );

            const querySnapshot = await getDocs(q); // Fetch documents based on the query
            return querySnapshot.docs.map((doc) => ({
              type: "added", // Add a default type to match `DocumentChangeAction`
              payload: { doc }, // Preserve the document snapshot structure
            }));
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          map((actions: DocumentChangeAction<unknown>[]) => this.snapShotData(actions)) // Pass correct structure to `snapShotData`
        )
        : of([]);
    })
  );

  public getInstantWinData$: Observable<Data> = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      return user
        ? this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return []; // Return an empty array if offline

            const q = query(
              collection(this.firestore, `/general_modules/game/${environment.mirrorPath}instant_win`), // Reference Firestore collection
              where("game_status", "==", 1),
              where("is_avilable_to_purchase", "==", zeroIndex)
            );

            const querySnapshot = await getDocs(q); // Fetch documents matching the query
            return querySnapshot.docs.map((doc) => ({
              type: "added", // Add default `type` to match DocumentChangeAction
              payload: { doc }, // Mimic structure of AngularFirestore's DocumentChangeAction
            }));
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          map((actions: DocumentChangeAction<unknown>[]) => this.snapShotData(actions)) // Pass correct structure to `snapShotData`
        )
        : of([]);
    })
  );

  public getCasualData$: Observable<Data> = this.userManagementService?.userRequest$.pipe(
    switchMap((user) => {
      return user
        ? this.catchService.isOnline$.pipe(
          switchMap(async (isOnline) => {
            if (!isOnline) return []; // Return an empty array if offline

            const q = query(
              collection(this.firestore, `/general_modules/game/${environment.mirrorPath}skill`), // Reference Firestore collection
              where("game_status", "==", 1),
              where("is_avilable_to_purchase", "==", zeroIndex)
            );

            const querySnapshot = await getDocs(q); // Fetch documents matching the query
            return querySnapshot.docs.map((doc) => ({
              type: "added", // Add default `type` to match DocumentChangeAction
              payload: { doc }, // Mimic structure of AngularFirestore's DocumentChangeAction
            }));
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          map((actions: DocumentChangeAction<unknown>[]) => this.snapShotData(actions)) // Pass correct structure to `snapShotData`
        )
        : of([]);
    })
  );
  getGamesData$ = combineLatest([this.getRapidFireData$, this.getInstantWinData$, this.getCasualData$]);

  calculateTimeRemaining(expirySeconds: number): string {
    const currentTime = Math.floor(Date.now() / 1000);
    const offset = 0;
    const timeDiff = expirySeconds - currentTime - offset;
    //const days = Math.floor(timeDiff / (60 * 60 * 24));
    const hours = Math.floor((timeDiff % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((timeDiff % (60 * 60)) / 60);
    if (hours <= 0 && minutes <= 0) {
      return "Expired";
    }
    return `${hours}h ${minutes}m`;
  }

  calculateProgressPercentage(achieved: number, target: number): number {
    return (achieved / target) * 100;
  }

  isExpired(expirySeconds: number): boolean {
    try {
      const currentTime = Math.floor(Date.now() / 1000);
      if (expirySeconds <= currentTime) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      return true;
    }
  }
  // Fetches game data from Firestore and updates the gameDataSignal with the retrieved data.
  fetchGameData(gameDoc = 'game'): void {

    this.commonService.getUid("fetchGameData")
      .pipe(takeUntil(this.destroySubscription$))
      .subscribe({
        next: (userId) => {
          if (!userId) {
            return;
          }
          const docRef = doc(this.firestore, `/users/${userId}/main_pages/${gameDoc}`);

          // Listen to Firestore changes
          onSnapshot(
            docRef,
            (docSnap) => {
              const data = docSnap.data();
              if (!data) {
                return;
              }

              // Set the signal only when data exists
              this.gameDataSignal.set({ id: docSnap.id, ...((data || {}) as Data) });
            },
            (err) => {
              console.error("[fetchGameData] Error fetching game data:", err);
            }
          );
        },
        error: (err) => console.error("[fetchGameData] Error retrieving userId:", err),
      });
  }

  // Returns the current state of the gameDataSignal.
  getGameDataSignal() {
    return this.gameDataSignal;
  }

  fetchDevGameData(gameDoc: string = 'game'): void {
    this.commonService.getUid("fetchGameData")
      .pipe(takeUntil(this.destroySubscription$))
      .subscribe({
        next: (userId) => {
          if (!userId) {
            return;
          }
          const docRef = doc(this.firestore, `/users/${userId}/main_pages/${gameDoc}`);

          // Listen to Firestore changes
          onSnapshot(
            docRef,
            async (docSnap) => {
              const data = docSnap.data();
              if (!data) {
                return;
              }
              // Set the signal only when data exists
              this.devGameDataSignal.set({ id: docSnap.id, ...((data || {}) as Data) });
            },
            (err) => {
              console.error("[fetchGameData] Error fetching game data:", err);
            }
          );
        },
        error: (err) => console.error("[fetchGameData] Error retrieving userId:", err),
      });
  }

  // Returns the current state of the devGameDataSignal.
  getDevGameDataSignal() {
    return this.devGameDataSignal;
  }

  fetchProfileTab(): void {
    this.authService.getUid$("GamesFeedSampleService").subscribe({
      next: (userId) => {
        if (!userId) {
          return;
        }

        const docRef = doc(this.firestore, `/users/${userId}/balance/current`);
        onSnapshot(
          docRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data() as Data | undefined;
              this.gameDataProfile.set({ id: docSnap.id, ...(data || {}) });
            }
          },
          (err) => {
            return err;
          }
        );
      },
      error: (err) => {
        return err;
      },
    });
  }

  // Returns the current state of the gameDataProfile signal.
  getGameProfileSignal() {
    return this.gameDataProfile;
  }

  logChallengeLookupData() {
    defer(() => {
      const docRef = doc(this.firestore, "/Global_lookup/challenge_lookup");

      return new Observable<void>((observer) => {
        const unsubscribe = onSnapshot(
          docRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data() as Data;
              this.dataSignal.set(data); // Update the signal with new data
            }
          },
          (err) => {
            console.error("Error fetching challenge lookup data:", err);
            observer.error(err);
          }
        );

        return () => unsubscribe(); // Cleanup
      });
    }).subscribe();
  }
  getdataSignal() {
    return this.dataSignal;
  }

  handleGamesPageVisit() {
    const today = new Date().toISOString().split("T")[0];

    this.authService?.userDetails$.pipe(take(1)).subscribe(async (userDetails: Data) => {
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap(async (userId) => {
          if (!userId) {
            console.error("No userId found in Firestore.");
            return;
          }
          const visitCount = (userDetails?.game_page_visited?.[today] || 0) + 1;
          const visitData = { [today]: visitCount };

          try {
            const path_id = "yzF1Ss5eiMqnh91zQMtv";
            const doc_data = { game_page_visited: visitData };
            const method = "update";

            await this.authService
              .getV2CloudFunction("updatefirestoredoc", {
                path_id,
                doc_data,
                method,
              })
              .subscribe({
                error: (error) => {
                  console.error("Error updating game page visit data:", error);
                },
              });
          } catch (err) {
            console.error("Error updating game page visit data:", err);
            return err;
          }

          if (visitData[today] === 2) {
            this.hasPlayedDailyWheelToday();
          }
          return of(userId) || of(null);
        })
      );
    });
  }

  async hasPlayedDailyWheelToday() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);
      return this.authService.getUid$("GamesFeedSampleService").pipe(
        switchMap((userId) => {
          const sessionsRef = collection(this.firestore, `/users/${userId}/game_play_session`);
          const gamePlaySessionData = query(
            sessionsRef,
            where("parent_game_id", "==", 655),
            where("session_start", ">=", sevenDaysAgo),
            where("session_start", "<", today) // Query to filter and sort inbox
          );

          return getDocs(gamePlaySessionData).then(async (snapshot) => {
            if (!snapshot.empty) {
              // At least one session found in the last 7 days
              const translatedMsg = await this.authService.getConstantsMsg("1156");
              const message = "Don't miss out! Keep your streak alive to win bigger prizes!";
              const defaultMsg = {
                message: {
                  line_1: message,
                  line_2: "",
                },
                style: {
                  icon: "tick circle info.svg",
                  type: "Info",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
              return false;
            } else {
              return true; // User has not played in the last 7 days
            }
          });
        })
      );
    } catch (error) {
      console.error("Error checking daily wheel play status:", error);
    }
  }

  userMainPage(gameNewDoc: string = 'game'): Observable<{ id: string, data: Data } | null> {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        if (!userId) {
          return of(null);
        }
        const docRef = doc(this.firestore, `/users/${userId}/main_pages/${gameNewDoc}`);
        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) {
              return of(null);
            }
            // Use docSnapshots to get an Observable stream of document snapshots
            return docSnapshots(docRef).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              map((docSnapshot: DocumentSnapshot<DocumentData>) => {
                if (docSnapshot && docSnapshot.exists()) {
                  return { id: docSnapshot.id, data: docSnapshot.data() as Data };
                }
                return null;
              })
            );
          }),
          catchError((err) => {
            return throwError(() => {
              return err;
            });
          }),
          shareReplay(1)
        );
      })
    );
  }

  updateGameData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      // Real-time Firestore listener for the document
      return new Observable<Data>((observer) => {
        const docRef = doc(this.firestore, `/general_modules/${environment.mirrorPath}game`);
        const unsubscribe = onSnapshot(
          docRef,
          (docSnap) => {
            observer.next(docSnap.exists() ? (docSnap.data() as Data) : {});
          },
          (error) => observer.error(error)
        );
        return () => unsubscribe();
      }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    switchMap((globalData: Data) => {
      if (globalData) {
        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  if (!userId) {
                    return of(null);
                  }
                  const req = {
                    userId: userId,
                    page: "game",
                    method: "createGames",
                  };
                  this.authService.updateMainPageSubscription = this.authService
                    .updateMainPages(req)
                    .pipe(takeUntil(this.destroySubscription$))
                    .subscribe();
                  return of(userId) || of(null);
                })
              ).subscribe();
            }
          })
        );
      }
      return EMPTY;
    })
  );

  updateRapidFireGameData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      // Real-time Firestore listener for the collection
      return new Observable<Data[]>((observer) => {
        const q = query(collection(this.firestore, `/general_modules/game/${environment.mirrorPath}rapid_fire`));
        const unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            const docs = querySnapshot.docs.map((doc) => doc.data());
            observer.next(docs);
          },
          (error) => observer.error(error)
        );
        return () => unsubscribe();
      }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    delay(8000),
    switchMap((globalDataArray: Data[]) => {
      const globalData = globalDataArray?.[0];
      if (globalData) {
        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  if (!userId) {
                    return of(null);
                  }
                  const req = {
                    userId: userId,
                    page: "game",
                    method: "createGames",
                  };
                  this.authService.updateMainPageSubscription = this.authService
                    .updateMainPages(req)
                    .pipe(takeUntil(this.destroySubscription$))
                    .subscribe();
                  const timeoutId = setTimeout(() => {
                    req.method = "createRecommendations";
                    this.updateMainpageSubTwo = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();
                    this.updateMainpageSubTwo.unsubscribe();
                    clearTimeout(timeoutId);
                  }, 3000);
                  return of(userId) || of(null);
                })
              ).subscribe();
            }
          })
        );
      }
      return EMPTY;
    })
  );

  updateInstantWinGameData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      return new Observable<Data[]>((observer) => {
        const q = query(collection(this.firestore, `/general_modules/game/${environment.mirrorPath}instant_win`));
        const unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            const docs = querySnapshot.docs.map((doc) => doc.data());
            observer.next(docs);
          },
          (error) => observer.error(error)
        );
        return () => unsubscribe();
      }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    delay(8000),
    switchMap((globalDataArray: Data[]) => {
      const globalData = globalDataArray?.[0];
      if (globalData) {
        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  if (!userId) {
                    return of(null);
                  }
                  const req = {
                    userId: userId,
                    page: "game",
                    method: "createGames",
                  };
                  this.authService.updateMainPageSubscription = this.authService
                    .updateMainPages(req)
                    .pipe(takeUntil(this.destroySubscription$))
                    .subscribe();
                  const timeoutId = setTimeout(() => {
                    req.method = "createRecommendations";
                    this.updateMainpageSubTwo = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();
                    this.updateMainpageSubTwo.unsubscribe();
                    clearTimeout(timeoutId);
                  }, 3000);
                  return of(userId) || of(null);
                })
              ).subscribe();
            }
          })
        );
      }
      return EMPTY;
    })
  );

  updateSkillGameData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      // Real-time Firestore listener for the collection
      return new Observable<Data[]>((observer) => {
        const q = query(collection(this.firestore, `/general_modules/game/${environment.mirrorPath}skill`));
        const unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            const docs = querySnapshot.docs.map((doc) => doc.data());
            observer.next(docs);
          },
          (error) => observer.error(error)
        );
        return () => unsubscribe();
      }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    delay(8000),
    switchMap((globalDataArray: Data[]) => {
      const globalData = globalDataArray?.[0];
      if (globalData) {
        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  const req = {
                    userId: userId,
                    page: "game",
                    method: "createGames",
                  };
                  this.authService.updateMainPageSubscription = this.authService
                    .updateMainPages(req)
                    .pipe(takeUntil(this.destroySubscription$))
                    .subscribe();
                  const timeoutId = setTimeout(() => {
                    req.method = "createRecommendations";
                    this.updateMainPageSubThree = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();
                    this.updateMainPageSubThree.unsubscribe();
                    clearTimeout(timeoutId);
                  }, 3000);
                  return of(userId) || of(null);
                })
              ).subscribe();
            }
          })
        );
      }
      return EMPTY;
    })
  );

  updateGameOrderData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;

      return defer(() =>
        new Observable<Data>((observer) => {
          const docRef = doc(this.firestore, "/Global_lookup/games_order");
          const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
              observer.next(docSnap.exists() ? (docSnap.data() as Data) : {});
            },
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError)
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    switchMap((globalData: Data) => {
      if (globalData) {
        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService.getUid$("GamesFeedSampleService").pipe(
                switchMap((userId) => {
                  const req = {
                    userId: userId,
                    page: "game",
                    method: "createGames",
                  };
                  this.authService.updateMainPageSubscription = this.authService
                    .updateMainPages(req)
                    .pipe(takeUntil(this.destroySubscription$))
                    .subscribe();
                  return of(userId) || of(null);
                })
              ).subscribe();
            }
          })
        );
      }
      return EMPTY;
    })
  );

  updateGameRecommendationData$: Observable<void> = defer(() =>
    this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return EMPTY;

        return new Observable<Data[]>((observer) => {
          const q = query(
            collection(
              this.firestore,
              "/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation"
            )
          );

          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              const docs = querySnapshot.docs.map((doc) => doc.data());
              observer.next(docs);
            },
            (error) => observer.error(error)
          );

          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      switchMap((globalDataArray: Data[]) => {
        const globalData = globalDataArray?.[0];
        if (globalData) {
          return this.userMainPage().pipe(
            take(1),
            map((userMainPageResult) => {
              const userMainPageData = userMainPageResult?.data;
              if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
                this.authService
                  .getUid$("GamesFeedSampleService")
                  .pipe(
                    switchMap((userId) => {
                      if (!userId) return of(null);

                      const req = {
                        userId: userId,
                        page: "game",
                        method: "createRecommendations",
                      };

                      this.authService.updateMainPageSubscription = this.authService
                        .updateMainPages(req)
                        .pipe(takeUntil(this.destroySubscription$))
                        .subscribe();

                      return of(userId) || of(null);
                    })
                  )
                  .subscribe(); // ⚠️ subscribing inside map, careful!
              }
            })
          );
        }
        return EMPTY;
      })
    )
  );

  // Now using onSnapshot for real-time Firestore updates
  updateGameBannerData$: Observable<void> = defer(() => {
    const path = "/general_modules/game_promotional_slider/game_slider";

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return EMPTY;

        return new Observable<Data[]>((observer) => {
          const q = query(collection(this.firestore, path));
          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              observer.next(querySnapshot.docs.map((doc) => doc.data()));
            },
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      switchMap((globalDataArray: Data[]) => {
        const globalData = globalDataArray?.[0];
        if (!globalData) return EMPTY;

        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.game_banner_update_timestamp?.toDate()) {
              this.authService
                .getUid$("GamesFeedSampleService")
                .pipe(
                  switchMap((userId) => {
                    if (!userId) return of(null);

                    const req = {
                      userId,
                      page: "game",
                      method: "createGameBanners",
                    };

                    this.authService.updateMainPageSubscription = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();

                    return of(userId);
                  })
                )
                .subscribe(); // explicitly subscribe to execute
            }
          })
        );
      })
    );
  });

  updateGameRecommendationRetoolData$: Observable<void> = defer(() =>
    this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return EMPTY;
        // Real-time Firestore listener for the collection
        return new Observable<Data[]>((observer) => {
          const q = query(
            collection(
              this.firestore,
              "/general_modules/game/recommendation/new_recommendation/last_played_game_recommendation_retool"
            )
          );
          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              const docs = querySnapshot.docs.map((doc) => doc.data());
              observer.next(docs);
            },
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      switchMap((globalDataArray: Data[]) => {
        const globalData = globalDataArray?.[0];
        if (globalData) {
          return this.userMainPage().pipe(
            take(1),
            map((userMainPageResult) => {
              const userMainPageData = userMainPageResult?.data;
              if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
                this.authService.getUid$("GamesFeedSampleService").pipe(
                  switchMap((userId) => {
                    if (!userId) return of(null);
                    const req = {
                      userId: userId,
                      page: "game",
                      method: "createRecommendations",
                    };
                    this.authService.updateMainPageSubscription = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();
                    return of(userId) || of(null);
                  })
                ).subscribe();
              }
            })
          );
        }
        return EMPTY;
      })
    )
  );

  updateGameBannerRetoolData$: Observable<void> = defer(() => {
    const path = "/general_modules/game_promotional_slider/promotional_slider_dynamic_game";

    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return EMPTY;

        return new Observable<Data[]>((observer) => {
          const q = query(collection(this.firestore, path));
          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              observer.next(querySnapshot.docs.map((doc) => doc.data()));
            },
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      switchMap((globalDataArray: Data[]) => {
        const globalData = globalDataArray?.[0];
        if (!globalData) return EMPTY;

        return this.userMainPage().pipe(
          take(1),
          map((userMainPageResult) => {
            const userMainPageData = userMainPageResult?.data;
            if (userMainPageData && globalData?.update_timestamp?.toDate() > userMainPageData?.update_timestamp?.toDate()) {
              this.authService
                .getUid$("GamesFeedSampleService")
                .pipe(
                  switchMap((userId) => {
                    if (!userId) return of(null);

                    const req = {
                      userId,
                      page: "game",
                      method: "createGameBanners",
                    };

                    this.authService.updateMainPageSubscription = this.authService
                      .updateMainPages(req)
                      .pipe(takeUntil(this.destroySubscription$))
                      .subscribe();

                    return of(userId);
                  })
                )
                .subscribe(); // explicitly trigger execution
            }
          })
        );
      })
    );
  });

  public async checkUserGamePoints(userGamePoint: number, gamePrice: number): Promise<boolean> {
    if (userGamePoint < gamePrice) {
      const translatedMsg = await this.authService.getConstantsMsg("1149");
      const message = "You do not have enough points to complete this transaction";
      const defaultMsg = {
        message: {
          line_1: message,
          line_2: "",
        },
        style: {
          icon: "tick circle error.svg",
          type: "Error",
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
      return false;
    }
    return true;
  }
  // This method has been used to get Game Prize box Settings Data
  getGamePrizeBoxSettingsData(): void {
    const docRef = doc(this.firestore, "/Global_lookup/game_prize_box_settings");

    defer(() =>
      docData(docRef).pipe(
        catchError(() => {
          return EMPTY; // Return empty observable on error to prevent invalid stream errors
        }),
        takeUntil(this.destroySubscription$),
        switchMap((data: any) => {
          if (data) {
            this.getGamePrizeBoxSettings.set(data); // Cache or update the settings
            return of(data); // ✅ Return Observable instead of array
          }
          return EMPTY; // Return empty if no data exists
        })
      )
    ).subscribe({
      next: (data) => { },
      error: (err) => {
        console.error("Error in game prize box settings subscription:", err);
      },
    });
  }
  // Get Game Prize Box Signal
  getGamePrizeBoxSignal() {
    return () => this.getGamePrizeBoxSettings(); // <-- note the () to read the signal
  }

  triggerPointsUsageInbox() {
  return this.authService.getUid$("GamesFeedSampleService").pipe(
    switchMap((userId) => {
            if (!userId) {
        console.error("User ID not found in Firestore.");
        return EMPTY;
      }
      const ttl = Timestamp.fromMillis(dayjs().add(2, "day").valueOf()); // Expires in 2 days
        const creationTimestamp = Timestamp.fromDate(new Date()); // Current time

      const path_id = "8MeSnBfFkPn93kXGFJ0g";
      const doc_data = {
        messages: ["inbox_points_usage"],
        creation_timestamp: creationTimestamp,
        ttl: ttl,
      };

      return this.authService.getV2CloudFunction(
        "updatefirestoredoc",
        {
          path_id,
          doc_data,
          method: "create",
        }
      );
    }),
    catchError((error) => {
      console.error(
        "Error triggering inbox points usage message:",
        error
      );
      return EMPTY;
    })
  );
}
  getPremiumGamePromoDetails(): Observable<Data> {
    return this.authService.getUid$("GamesFeedSampleService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("User ID not found in localStorage.");
          // return empty observable so stream doesn’t break
          return of({} as Data);
        }

        return new Observable<Data>((observer) => {
          const docRef = doc(this.firestore, `users/${userId}/low_points_on_premium_game/status`);

          const unsubscribe = onSnapshot(
            docRef,
            (snapshot) => {
              observer.next(snapshot.exists() ? (snapshot.data() as Data) : ({} as Data));
            },
            (error) => observer.error(error)
          );

          // Cleanup subscription on unsubscribe
          return () => unsubscribe();
        }).pipe(shareReplay(1));
      })
    );
  }

  async updatePromoStatus(promoToUpdate: string, promoStatus: boolean): Promise<any[]> {
    try {
      const path_id = "1cblQl68QltRugb1YDAY";
      const method = "read";

      // Wait for Firestore document
      const docSnap: any = await firstValueFrom(
        this.authService
          ?.getV2CloudFunction("updatefirestoredoc", { path_id, method })
          .pipe(map((snapshot: any) => snapshot?.data))
      );

      if (!docSnap) return [];

      const promoDetails = docSnap?.promo_details || [];
      const updatedPromoDetails = promoDetails.map((promo: any) => {
        if (promo.promo_name === promoToUpdate) {
          return { ...promo, status: promoStatus };
        }
        return promo;
      });

      // Update Firestore with modified promo_details
      await firstValueFrom(
        this.authService
          .getV2CloudFunction("updatefirestoredoc", {
            path_id: "1cblQl68QltRugb1YDAY",
            doc_data: { promo_details: updatedPromoDetails },
            method: "update",
          })
          .pipe(catchError((error) => of(error)))
      );

      return updatedPromoDetails;
    } catch (error) {
      console.error("Error updating promo status:", error);
      throw error;
    }
  }
}
